import {Fragment, useRef} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
import Masonry from "react-masonry-component";
import InfiniteScroll from 'react-infinite-scroll-component';
import {Link, useParams} from 'react-router-dom';
import {getScrollGifs} from '../../api/apiClient';
import GifCard from '../gif-card/GifCard';
import ModalView from '../modal-view/ModalView';
import ShareSocial from '../share-social/ShareSocial';
import {useModal} from '../../context/ModalContext';
import useWindowSize from '../../hooks/useWindowSize';
import {useAuth} from '../../context/AuthContext';
import NoGifFound from '../no-gif-found/NoGifFound';
import Classes from '../gif-card/GifCard.module.scss';
import styles from '../../pages/artist/Artist.module.scss';
import InfiniteLoader from './infinite-loader/InfiniteLoader';

const GifInfiniteScroll = (props: any) => {
    const {apiPath, itemsPerPage, onClickRelatedCard, favLinkShare, relatedTagsView, cardItemClass = '', sticker} = props;
    const {getToken} = useAuth();
    const cardItemRef: any = useRef();
    const params = useParams();
    const {width, height} = useWindowSize();
    const {showCardShareModal, handleCardShareModalClose, showCardShareID} = useModal();
    const token = getToken();

    // check browser. Is it safari or not
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        data,
        isError,
        isLoading
    }: any = useInfiniteQuery([apiPath], ({pageParam = 0}) => getScrollGifs(apiPath, pageParam, itemsPerPage, token), {
        useErrorBoundary: true,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage?.next_page !== -1 ? allPages.length : false;
        },
    });

    //== Masonry Options
    const masonryOptions: any = {
        fitWidth: false,
        gutter: width <= 576 ? 6 : 20,
        resize: true,
        transitionDuration: 0,
        itemSelector: '.cardItem',
    }

    //== Derive a stable CSS aspect-ratio from the stored "WxH" dimensions.
    //== This replaces the previous height calc that multiplied by a DOM-measured
    //== clientWidth read during render: that read was `undefined` (-> NaN height)
    //== whenever no re-render followed the first paint (e.g. cached query data),
    //== which made cards render at full media size and the list grow endlessly.
    //== Some records also carry corrupt dimensions (e.g. "5x68" for a real
    //== 250x141 gif); such tiny/implausible values would create a card thousands
    //== of px tall, so we reject them and fall back to a square.
    const cardAspectRatio = (dimensions: any): string => {
        const [w, h] = String(dimensions ?? '').split('x').map(Number);
        if (!(w >= 20) || !(h >= 20)) return '1 / 1';
        return `${w} / ${h}`;
    }
    return (
        <Fragment>
            {isLoading && (
                <ul className={`${Classes.gifCardPlaceholder} dashboardCardLoader`}>
                    {Array(height > 1400 ? 40 : 20).fill(1).map((_, i) => (
                        <li key={i} ref={cardItemRef}>
                            <Skeleton style={{marginBottom: '10px'}}/>
                        </li>
                    ))}
                </ul>
            )}
            {!isLoading && isError && (
                <div className={`${styles.notFoundErrorBox} d-inline-flex justify-content-center mt-5`}>
                    <h2 className={'d-inline-block'}>
                        Something went wrong! Back <Link to={'/'}>Home</Link>
                    </h2>
                </div>
            )}
            {!isLoading && !isError && data?.pages[0]?.resources?.length <= 0 && (<NoGifFound/>)}
            {!isLoading && !isError && data?.pages[0]?.resources?.length > 0 && (
                <InfiniteScroll
                    className={'overflow-hidden'}
                    next={fetchNextPage}
                    hasMore={hasNextPage}
                    loader={
                        <div className={'mx-auto my-3 py-3'} style={{maxWidth: '60px'}}>
                           <InfiniteLoader/>
                        </div>
                    }
                    dataLength={isFetchingNextPage}
                    endMessage={''}

                >
                    {/*@ts-ignore*/}
                    <Masonry
                        className={'cardList'}
                        elementType={"ul"}
                        options={masonryOptions}
                        disableImagesLoaded={true}
                        updateOnEachImageLoad={true}
                        enableResizableChildren={true}
                    >
                        {data?.pages?.map((pg: any, index: number) => (
                            <Fragment key={index}>
                                {pg?.resources?.map((gif: any) => {
                                    if (gif?.slug !== params?.slug) {
                                        return (<Fragment key={gif?._id}>
                                                <li
                                                    ref={cardItemRef}
                                                    className={`cardItem ${cardItemClass}`}
                                                    style={{
                                                        aspectRatio: cardAspectRatio(gif?.originalDimensions)
                                                    }}
                                                    onClick={onClickRelatedCard}
                                                >
                                                    <GifCard
                                                        {...gif}
                                                        favLinkShare={favLinkShare}
                                                        queryInvalidateKey={apiPath}
                                                        relatedTagsView={relatedTagsView}
                                                        sticker={sticker || gif?.isSticker}
                                                    />
                                                </li>

                                                {(showCardShareID === gif?._id) && showCardShareModal && (
                                                    <ModalView
                                                        show={showCardShareModal}
                                                        handleClose={() => handleCardShareModalClose(props?._id)}
                                                        additionalClassName={`${Classes.shareModalWrapModalCard} p-2 p-md-3`}
                                                        centered={false}
                                                    >
                                                        <div className="shareModalWrap text-center">
                                                            <div className={gif?.isSticker ? Classes.stickerCard : ''}>
                                                                {(isSafari || gif?.isSticker) ? (
                                                                    <img
                                                                        src={`${process.env.REACT_APP_MEDIA_SERVER}/${gif.filePath}`}
                                                                        alt={gif.title}
                                                                        className={`${Classes.shareModalThumb} img-fluid rounded w-100`}
                                                                    />
                                                                ) : (
                                                                    <video
                                                                        autoPlay
                                                                        muted
                                                                        loop
                                                                        playsInline={true}
                                                                        preload={"metadata"}
                                                                        controls={false}
                                                                        src={`${process.env.REACT_APP_MEDIA_SERVER}/${gif.filePath}.mp4`}
                                                                        className={`${Classes.shareModalThumb} img-fluid rounded w-100`}
                                                                    />
                                                                )}

                                                            </div>
                                                            <ShareSocial
                                                                resourceData={gif}
                                                                width={width}
                                                                className={'justify-content-center mt-2 pb-0 border-0'}
                                                            />
                                                        </div>
                                                    </ModalView>
                                                )} </Fragment>
                                        )
                                    }
                                })}
                            </Fragment>
                        ))}
                    </Masonry>
                </InfiniteScroll>
            )}
        </Fragment>
    );
};

export default GifInfiniteScroll;